"use client";

import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { message } from "antd";

import AppLayoutDoctor from "../../../navigation/Layout/LayoutDoctor";
import { useTour } from "../../hooks/useTour";
import { appointmentsTourSteps } from "./appointmentsTourConfig";
import useDoctorPendingRdv from "../../hooks/useDoctorPendingRdv";

import ConfirmDeleteModal from "../../components/ConfirmDeleteModal/ConfirmDeleteModal";
import AppointmentTabs from "../../components/appointmentsDoctor/AppointmentDoctorTabs/AppointmentDoctorTabs";
import AppointmentCard from "../../components/appointmentsDoctor/AppointmentCard/AppointmentCard";
import PendingRequestsHero from "../../components/appointmentsDoctor/PendingRequestsHero/PendingRequestsHero";

import { getCurrentDoctor } from "../../../authDoctor/redux/thunks/doctorsThunks";
import useRendezvous from "../../hooks/useRendezvous";

import "./AppointmentsDoctor.css";

// Référence stable : un `localItems = []` en prop par défaut crée un
// tableau neuf à chaque render, ce qui invalidait les memos de
// useDoctorPendingRdv et bloquait la navigation (boucle de render).
const EMPTY_LOCAL_ITEMS = [];

const resolveDoctorId = (doctor) => {
  if (!doctor) return null;
  const raw =
    doctor.pgId ??
    doctor.doctorId ??
    doctor.medecinId ??
    doctor.id ??
    doctor.raw?.pgId ??
    doctor.raw?.doctorId ??
    doctor.raw?.id ??
    null;
  if (!raw) return null;
  const n = Number(String(raw).trim());
  return Number.isFinite(n) && n > 0 ? n : null;
};

export default function AppointmentsDoctor({
  LayoutComponent = AppLayoutDoctor,
  disableRemote = false,
  localItems = EMPTY_LOCAL_ITEMS,
  medecinIdOverride = null,
}) {
  const dispatch = useDispatch();
  const { t } = useTranslation("appointments");
  const [searchParams] = useSearchParams();

  const [currentDoctor, setCurrentDoctor] = useState(null);
  const [doctorLoading, setDoctorLoading] = useState(true);
  const [doctorError, setDoctorError] = useState("");
  const [shouldStartTour, setShouldStartTour] = useState(false);

  useEffect(() => {
    if (disableRemote || medecinIdOverride) {
      setDoctorLoading(false);
      setDoctorError("");
      setCurrentDoctor(null);
      return;
    }

    let mounted = true;
    const loadCurrentDoctor = async () => {
      try {
        setDoctorLoading(true);
        setDoctorError("");
        const doctor = await dispatch(getCurrentDoctor()).unwrap();
        if (mounted) setCurrentDoctor(doctor);
      } catch (err) {
        if (mounted)
          setDoctorError(typeof err === "string" ? err : err?.message || t("page.loadDoctorError"));
      } finally {
        if (mounted) setDoctorLoading(false);
      }
    };
    loadCurrentDoctor();
    return () => { mounted = false; };
  }, [dispatch, t, disableRemote, medecinIdOverride]);

  const medecinId = useMemo(() => {
    if (disableRemote) return null;
    if (medecinIdOverride) return medecinIdOverride;
    return resolveDoctorId(currentDoctor);
  }, [currentDoctor, disableRemote, medecinIdOverride]);

  const {
    items = [],
    loading,
    error,
    reload,
    accept,
    cancel,
    updateItemStatus,
    medecinId: resolvedId,
  } = useDoctorPendingRdv(medecinId, {
    disableRemote,
    localItems,
  });

  const finalMedecinId = disableRemote
    ? "assistant-local"
    : resolvedId || medecinId || medecinIdOverride;

  const { rescheduleAppointment } = useRendezvous();

  const [query, setQuery]               = useState("");
  const [sort, setSort]                 = useState("asc");
  const [activeTab, setActiveTab]       = useState("incoming");
  const [viewMode, setViewMode]         = useState("list");
  const [statusFilter, setStatusFilter] = useState("");
  const [patientFilter, setPatientFilter] = useState("");
  const [busy, setBusy]                 = useState({});
  const [confirmState, setConfirmState] = useState({ open: false, item: null });
  const highlightId = searchParams.get("highlight");

  useEffect(() => {
    if (searchParams.get("tab") === "upcoming") setActiveTab("upcoming");
  }, [searchParams]);

  // ── Tour ──────────────────────────────────────────────────────────
  useEffect(() => {
    const hasGuide = searchParams.get("guide") === "true";
    const hasTour  = searchParams.get("tour")  === "appointments";
    if (hasGuide || hasTour) setTimeout(() => setShouldStartTour(true), 1000);
  }, [searchParams]);
  useTour(shouldStartTour, appointmentsTourSteps);

  // ── ✅ Listen for patient-side cancel / reschedule (cross-tab) ──────
  useEffect(() => {
    const CHANNEL_NAME = "medilink:appointments";
    const LS_KEY       = "medilink:appointment_action";

    const applyAction = ({ appointmentId, status, startTime, endTime }) => {
      if (!appointmentId || !status) return;

      // Optimistic update via localStorage override — instant UI flip
      updateItemStatus(appointmentId, status, {
        ...(startTime ? { startTime } : {}),
        ...(endTime   ? { endTime }   : {}),
      });

      // Reload with delay to let the server finish persisting the action
      setTimeout(() => {
        reload();
        setTimeout(() => reload(), 3000);
      }, 800);
    };

    // 1. BroadcastChannel — primary cross-tab communication
    let bc = null;
    try {
      bc = new BroadcastChannel(CHANNEL_NAME);
      bc.onmessage = (e) => applyAction(e.data || {});
    } catch (err) {
      console.warn("[Doctor] BroadcastChannel not supported:", err);
    }

    // 2. localStorage fallback for older browsers
    const handleStorage = (e) => {
      if (e.key !== LS_KEY || !e.newValue) return;
      try {
        applyAction(JSON.parse(e.newValue));
      } catch {}
    };
    window.addEventListener("storage", handleStorage);

    // 3. Same-tab fallback (patient and doctor in same tab)
    const handleLocal = (e) => applyAction(e.detail || {});
    window.addEventListener("appointment:patient_action", handleLocal);

    return () => {
      bc?.close();
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("appointment:patient_action", handleLocal);
    };
  }, [updateItemStatus, reload]);

  // ── Helpers ───────────────────────────────────────────────────────
  const normalize = (v) => String(v || "").toLowerCase();
  const getPatientSource = (appointment) => {
    const sources = [
      appointment?.source,
      appointment?.patientSource,
      appointment?.raw?.source,
      appointment?.raw?.patientSource,
    ].map(normalize);

    if (sources.includes("local")) return "local";
    if (sources.includes("medilink")) return "medilink";
    if (appointment?.state === "New patient") return "local";
    return "medilink";
  };
  const isPending   = (r) => { const s = normalize(r.status); return s === "scheduled" || s === "pending"; };
  const isConfirmed = (r) => { const s = normalize(r.status); return s === "confirmed" || s === "rescheduled"; };
  const isCancelled = (r) => normalize(r.status) === "cancelled";
  const isCompleted = (r) => normalize(r.status) === "completed";

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const toTs  = (r) => { const d = new Date(r.startTime || r.date); return Number.isNaN(d.getTime()) ? 0 : d.getTime(); };

  const stats = useMemo(() => ({
    incoming:         items.filter(isPending).length,
    upcoming:         items.filter((r) => { const d = new Date(r.startTime || r.date); return isConfirmed(r) && !Number.isNaN(d.getTime()) && d >= today; }).length,
    completed:        items.filter(isCompleted).length,
    cancelled:        items.filter(isCancelled).length,
    confirmedThisWeek: items.filter(isConfirmed).length,
  }), [items]);

  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    let base = [...items];

    if (activeTab === "incoming")  base = base.filter(isPending);
    if (activeTab === "upcoming")  base = base.filter((r) => { const d = new Date(r.startTime || r.date); return isConfirmed(r) && !Number.isNaN(d.getTime()) && d >= today; });
    if (activeTab === "completed") base = base.filter(isCompleted);
    if (activeTab === "cancelled") base = base.filter(isCancelled);
    if (statusFilter)  base = base.filter((r) => normalize(r.status) === statusFilter);
    if (patientFilter) base = base.filter((r) => getPatientSource(r) === patientFilter);
    if (q) base = base.filter((r) => r.name?.toLowerCase().includes(q) || r.email?.toLowerCase().includes(q) || r.phone?.toLowerCase().includes(q) || r.identifier?.toLowerCase().includes(q) || r.state?.toLowerCase().includes(q));

    base.sort((a, b) => sort === "desc" ? toTs(b) - toTs(a) : toTs(a) - toTs(b));
    return base;
  }, [items, query, sort, activeTab, statusFilter, patientFilter]);

  useEffect(() => {
    if (!highlightId || loading || error) return undefined;
    const appointment = filteredItems.find(
      (item) => String(item?.id ?? item?._id ?? "") === String(highlightId)
    );
    if (!appointment) {
      if (items.length) message.info("This appointment is no longer available.");
      return undefined;
    }

    const timer = window.setTimeout(() => {
      const target = Array.from(
        document.querySelectorAll("[data-appointment-id]")
      ).find((element) => element.dataset.appointmentId === String(highlightId));
      if (!target) {
        message.info("This appointment is no longer available.");
        return;
      }
      target.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
    return () => window.clearTimeout(timer);
  }, [error, filteredItems, highlightId, items.length, loading]);

  // ── Actions ───────────────────────────────────────────────────────
  const handleAccept = async (r) => {
    const rid = String(r.id);
    try {
      setBusy((b) => ({ ...b, [rid]: "accept" }));
      await accept(r.id);
    } finally {
      setBusy((b) => { const { [rid]: _, ...rest } = b; return rest; });
    }
  };

  const askCancel       = (r) => setConfirmState({ open: true, item: r });
  const closeConfirmModal = () => setConfirmState({ open: false, item: null });

  const confirmCancel = async () => {
    const r = confirmState.item;
    if (!r) return;
    closeConfirmModal();
    const rid = String(r.id);
    try {
      setBusy((b) => ({ ...b, [rid]: "cancel" }));
      await cancel(r.id);
    } finally {
      setBusy((b) => { const { [rid]: _, ...rest } = b; return rest; });
    }
  };

  const handleReschedule = async ({ appointment, startTime, endTime }) => {
    const rid = String(appointment.id);
    try {
      setBusy((b) => ({ ...b, [rid]: "reschedule" }));
      await rescheduleAppointment({
        appointment: { ...appointment, medecinId: finalMedecinId, doctorId: finalMedecinId },
        startTime,
        endTime,
      });
      updateItemStatus(appointment.id, "rescheduled", {
        startTime,
        endTime,
        date: new Date(startTime).toLocaleDateString("fr-FR"),
        time: new Date(startTime).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }),
      });
      reload();
      setActiveTab("upcoming");
    } catch (err) {
      console.error("❌ handleReschedule:", err);
    } finally {
      setBusy((b) => { const { [rid]: _, ...rest } = b; return rest; });
    }
  };

  // ── Loading / error guards ────────────────────────────────────────
  if (doctorLoading) {
    return (
      <LayoutComponent>
        <div className="appointments-doctor-page">
          <div className="appointments-doctor-container">
            <div className="appointments-board">
              <p className="appointments-state">{t("page.loadingDoctor")}</p>
            </div>
          </div>
        </div>
      </LayoutComponent>
    );
  }

  if (!disableRemote && !finalMedecinId) {
    return (
      <LayoutComponent>
        <div className="appointments-doctor-page">
          <div className="appointments-doctor-container">
            <div className="appointments-board">
              <p className="appointments-state">{doctorError || t("page.loadDoctorError")}</p>
              <pre style={{ marginTop: 12, whiteSpace: "pre-wrap" }}>
                {JSON.stringify(currentDoctor, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      </LayoutComponent>
    );
  }

  // ── Render ────────────────────────────────────────────────────────
  return (
    <LayoutComponent>
      <div className="appointments-doctor-page">
        <div className="appointments-doctor-container">
          <div className="appointments-board">

            <div className="appointments-board__header">
              <div>
                <h2>{t("page.title")}</h2>
                <p>{t("page.subtitle", { count: items.length })}</p>
              </div>
            </div>

            <PendingRequestsHero
              items={items.map((r) => ({ ...r, medecinId: finalMedecinId, doctorId: finalMedecinId }))}
              busy={busy}
              onConfirm={handleAccept}
              onCancel={askCancel}
              onReschedule={handleReschedule}
              medecinId={finalMedecinId}
            />

            <div className="appointments-board__top-actions">
              <div className="appointments-board__filters">
                <div className="appointments-board__search">
                  <svg viewBox="0 0 20 20" fill="none">
                    <circle cx="9" cy="9" r="6.5" stroke="currentColor" strokeWidth="1.5" />
                    <path d="M14 14l3.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                  <input
                    type="text"
                    placeholder={t("page.searchPlaceholder")}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                </div>

                <div className="appointments-board__select-wrap">
                  <select className="appointments-board__select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="">{t("page.allStatuses")}</option>
                    <option value="pending">{t("status.pending")}</option>
                    <option value="scheduled">{t("page.scheduled")}</option>
                    <option value="confirmed">{t("status.confirmed")}</option>
                    <option value="rescheduled">{t("status.rescheduled")}</option>
                    <option value="completed">{t("status.completed")}</option>
                    <option value="cancelled">{t("status.cancelled")}</option>
                  </select>
                </div>

                <div className="appointments-board__select-wrap">
                  <select className="appointments-board__select" value={patientFilter} onChange={(e) => setPatientFilter(e.target.value)}>
                    <option value="">{t("page.allPatients")}</option>
                    <option value="local">{t("page.local")}</option>
                    <option value="medilink">{t("page.medilink")}</option>
                  </select>
                </div>

                <div className="appointments-board__view-toggle">
                  <button type="button" className={`appointments-board__view-btn ${viewMode === "list" ? "active" : ""}`} onClick={() => setViewMode("list")} title={t("page.list")} aria-label={t("page.list")}>
                    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M8 6h13M8 12h13M8 18h13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      <path d="M3 6h.01M3 12h.01M3 18h.01" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                    </svg>
                  </button>
                  <button type="button" className={`appointments-board__view-btn ${viewMode === "grid" ? "active" : ""}`} onClick={() => setViewMode("grid")} title={t("page.grid")} aria-label={t("page.grid")}>
                    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M4 4h7v7H4V4ZM13 4h7v7h-7V4ZM4 13h7v7H4v-7ZM13 13h7v7h-7v-7Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            <AppointmentTabs activeTab={activeTab} setActiveTab={setActiveTab} stats={stats} />

            {loading && <p className="appointments-state">{t("page.loading")}</p>}

            {!loading && error && (
              <div className="appointments-error">
                <span>{String(error)}</span>
                <button type="button" onClick={reload}>{t("page.retry")}</button>
              </div>
            )}

            {!loading && !error && filteredItems.length === 0 && (
              <p className="appointments-state">{t("page.noResults")}</p>
            )}

            {viewMode === "list" && filteredItems.length > 0 && (
              <div className="appointments-table">
                <div className="appointments-table__head">
                  <span>{t("table.patient")}</span>
                  <span>{t("table.dateTime")}</span>
                  <span>{t("table.reason")}</span>
                  <span>{t("table.status")}</span>
                  <span>{t("table.actions")}</span>
                </div>
                {filteredItems.map((r) => (
                  <div
                    key={r.id}
                    data-appointment-id={r.id}
                    style={
                      String(r.id) === String(highlightId)
                        ? { outline: "3px solid #1677ff", borderRadius: 12 }
                        : undefined
                    }
                  >
                    <AppointmentCard
                      item={{ ...r, medecinId: finalMedecinId, doctorId: finalMedecinId }}
                      medecinId={finalMedecinId}
                      busy={busy[String(r.id)]}
                      onConfirm={() => handleAccept(r)}
                      onCancel={() => askCancel(r)}
                      onReschedule={handleReschedule}
                      viewMode="list"
                    />
                  </div>
                ))}
              </div>
            )}

            {viewMode === "grid" && filteredItems.length > 0 && (
              <div className="appointments-grid">
                {filteredItems.map((r) => (
                  <div
                    key={r.id}
                    data-appointment-id={r.id}
                    style={
                      String(r.id) === String(highlightId)
                        ? { outline: "3px solid #1677ff", borderRadius: 12 }
                        : undefined
                    }
                  >
                    <AppointmentCard
                      item={{ ...r, medecinId: finalMedecinId, doctorId: finalMedecinId }}
                      medecinId={finalMedecinId}
                      busy={busy[String(r.id)]}
                      onConfirm={() => handleAccept(r)}
                      onCancel={() => askCancel(r)}
                      onReschedule={handleReschedule}
                      viewMode="grid"
                    />
                  </div>
                ))}
              </div>
            )}

            <ConfirmDeleteModal
              open={confirmState.open}
              title={t("modal.cancelTitle")}
              message={t("modal.cancelMessage", { name: confirmState.item?.name || t("modal.cancelMessageDefault") })}
              confirmText={t("modal.confirmCancel")}
              cancelText={t("modal.close")}
              onCancel={closeConfirmModal}
              onConfirm={confirmCancel}
            />

          </div>
        </div>
      </div>
    </LayoutComponent>
  );
}